/*
 * ============================================================================
 * AMASS Electrolytes — Product page behaviour (US-005)
 * ============================================================================
 * Classic deferred script (loaded like global.js, NOT an ES module). It enhances
 * the AMASS PDP (sections/main-product.liquid) and INTEGRATES with Horizon's
 * native cart pipeline rather than replacing it:
 *
 *   - Add-to-Cart posts to Theme.routes.cart_add_url and dispatches the same
 *     'cart:update' CustomEvent Horizon's components use, so the US-004 cart
 *     drawer + free-shipping bar refresh and the drawer opens.
 *   - SIZE selection swaps the active Shopify variant (real variant price — no
 *     fake discount) and live-updates the price, the display-only "SAVE %"
 *     label, the unit-price line, and the Add-to-Cart label.
 *   - FLAVOR selection routes to the sibling PRODUCT per the US-001 routing map
 *     (RTD cans vs powders), preserving the selected size where it exists in the
 *     target product. Routing data comes from the markup (data-flavor-url +
 *     data-size), built server-side from product handles — never tag-based.
 *   - QUANTITY stepper, accordions, and ship-note are progressive enhancements.
 *
 * Pricing rule (verified in references.md): the "SAVE 15% / 20%" tier labels are
 * DISPLAY-ONLY marketing math. There is no Shopify discount engine behind size
 * tiers and no compare_at_price. We always render the real variant price and add
 * the real variant to cart; the SAVE label is computed from per-unit math purely
 * for display.
 * ============================================================================
 */
(function () {
  'use strict';

  /* ---- money formatting (Shopify money_format aware) --------------------- */
  function formatMoney(cents, format) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    var value = (cents / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    var noDecimals = Math.round(cents / 100).toLocaleString('en-US');
    var fmt = format || '${{amount}}';
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, token) {
      switch (token) {
        case 'amount':
        case 'amount_with_comma_separator':
          return value;
        case 'amount_no_decimals':
        case 'amount_no_decimals_with_comma_separator':
          return noDecimals;
        default:
          return value;
      }
    });
  }

  function routesRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function cartAddUrl() {
    if (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) {
      return window.Theme.routes.cart_add_url;
    }
    return routesRoot() + 'cart/add.js';
  }

  /* ======================================================================= */
  /* <amass-product> — the workhorse PDP buy-box controller                   */
  /* ======================================================================= */
  class AmassProduct extends HTMLElement {
    connectedCallback() {
      if (this.#booted) return;
      this.#booted = true;

      this.moneyFormat = this.getAttribute('data-money-format') || '${{amount}}';
      this.productUrl = this.getAttribute('data-product-url') || '';

      // Variant map emitted by snippets/variant-picker.liquid as an embedded JSON
      // <script> tag (so prices/ids are server-rendered).
      //   data-amass-variants-json -> [{size,id,price,available,unit,count,unitLabel}]
      this.variants = this.#readJson('[data-amass-variants-json]', []);

      // Subscribe map (US-005 round 2) from snippets/purchase-options.liquid:
      //   { discountPct, plans:[{id,name}], variants:{ <variantId>:{subPrice,wasPrice} } }
      // Read from the NATIVE selling-plan objects server-side — no hardcoded ids.
      this.subData = this.#readJson('[data-amass-sub-json]', null);
      this.hasSubscription = !!(this.subData && this.subData.variants);
      this.purchaseType = 'onetime';
      // True only while One-Time is in force because the selected variant cannot
      // subscribe — never because the shopper picked it. See #applySubscribeAvailability.
      this.purchaseTypeCoerced = false;

      this.priceEl = this.querySelector('[data-amass-price]');
      this.unitPriceEl = this.querySelector('[data-amass-unit-price]');
      this.atcLabelEl = this.querySelector('[data-amass-atc-label]');
      this.atcButton = this.querySelector('[data-amass-atc]');
      this.variantIdInput = this.querySelector('[data-amass-variant-id]');
      this.sellingPlanInput = this.querySelector('[data-amass-selling-plan]');
      this.qtyInput = this.querySelector('[data-amass-qty-input]');

      // Purchase-option controls (custom selector replacing the broken Loop widget).
      this.onetimePriceEl = this.querySelector('[data-amass-onetime-price]');
      this.subPriceEl = this.querySelector('[data-amass-sub-price]');
      this.subWasEl = this.querySelector('[data-amass-sub-was]');
      this.freqWrap = this.querySelector('[data-amass-freq-wrap]');
      this.freqSelect = this.querySelector('[data-amass-freq-select]');
      // Purchase fieldset — carries the subscribe-active state class that GATES
      // the delivery-frequency dropdown's visibility (CSS: hidden unless this
      // class is present). Replaces the old base rule that forced display:flex.
      this.purchaseEl = this.querySelector('[data-amass-purchase]');
      // Subscribe card + legend refs — hidden per-variant when Loop has not allocated
      // the plan to the selected variant (e.g. can 8-packs).
      this.subscribeCardEl = this.querySelector('[data-amass-purchase-card][data-purchase-type="subscribe"]');
      this.purchaseLegendEl = this.querySelector('[data-amass-purchase-legend]');

      // Sticky mobile Add-to-Cart bar (US-005 conversion) — lives inside this
      // element, so it shares all buybox state (variant id + selling_plan hidden
      // inputs, price). Refs cached here; wiring + scroll observer below.
      this.stickyBar = this.querySelector('[data-amass-sticky-bar]');
      this.stickyPriceEl = this.querySelector('[data-amass-sticky-price]');
      this.stickyLabelEl = this.querySelector('[data-amass-sticky-label]');
      this.stickyButton = this.querySelector('[data-amass-sticky-atc]');

      // Free-shipping nudge (US-005 conversion) — progress toward the $45 threshold
      // for the current selection; kept in sync on size / purchase-type / quantity.
      this.shipNudgeEl = this.querySelector('[data-amass-shipnudge]');
      // Back-in-stock sign-up card (snippets/amass-back-in-stock.liquid): a Klaviyo
      // embedded form shown only while the selected variant is sold out.
      this.notifyEl = this.querySelector('[data-amass-notify]');
      this.shipNudgeMsgEl = this.querySelector('[data-amass-shipnudge-msg]');
      this.shipNudgeFillEl = this.querySelector('[data-amass-shipnudge-fill]');

      this.#wireSizeSelector();
      this.#wireFlavorSelector();
      this.#wirePurchaseType();
      this.#wireQuantity();
      this.#wireAddToCart();
      this.#wireStickyBar();

      // If we arrived from a flavor switch carrying a size, pre-select it so the
      // chosen pack size is preserved across the sibling-product route.
      var carriedSize = this.#carriedSize();
      if (carriedSize) {
        var match = this.querySelector(
          'input[name="amass-size"][value="' + (window.CSS && CSS.escape ? CSS.escape(carriedSize) : carriedSize) + '"]'
        );
        if (match) {
          match.checked = true;
        }
      }

      // Initialise from the (possibly just-updated) selected size.
      var checked = this.querySelector('input[name="amass-size"]:checked');
      if (checked) {
        this.#applySize(checked.value, false);
      } else if (this.variantIdInput && this.variants.length) {
        // PDPs with no size selector never fire #applySize, so currentVariant would
        // stay undefined — and every subscribe gate below keys off it. Seed it from
        // the server-rendered variant id so those products can still subscribe.
        var seedId = parseInt(this.variantIdInput.value, 10);
        for (var i = 0; i < this.variants.length; i++) {
          if (this.variants[i].id === seedId) {
            this.currentVariant = this.variants[i];
            break;
          }
        }
        if (!this.currentVariant) this.currentVariant = this.variants[0];
        this.#applySubscribeAvailability();
        this.#refreshSubscribeDisplay();
      }

      // PURCHASE TYPE IS INITIALISED LAST, ON PURPOSE. It reads the server's checked
      // radio, which is now Subscribe & Save by default (subscription-first PDP), and
      // #applyPurchaseType only honours 'subscribe' when the SELECTED variant has an
      // allocation — a check that needs this.currentVariant. Running it before the
      // size init above (where currentVariant is set) silently coerced the default
      // back to One-Time on every load, undoing the server-rendered default.
      this.#initPurchaseType();
    }

    #booted = false;

    #readJson(selector, fallback) {
      var el = this.querySelector(selector);
      if (!el) return fallback;
      try {
        return JSON.parse(el.textContent.trim());
      } catch (e) {
        return fallback;
      }
    }

    #carriedSize() {
      try {
        var params = new URLSearchParams(window.location.search);
        return params.get('amass_size');
      } catch (e) {
        return null;
      }
    }

    /* ---- SIZE: swap variant, recompute price / SAVE / ATC label ---------- */
    #wireSizeSelector() {
      var self = this;
      this.querySelectorAll('input[name="amass-size"]').forEach(function (input) {
        input.addEventListener('change', function () {
          self.#applySize(input.value, true);
        });
      });
    }

    #findVariant(sizeValue) {
      for (var i = 0; i < this.variants.length; i++) {
        if (this.variants[i].size === sizeValue) return this.variants[i];
      }
      return null;
    }

    #applySize(sizeValue, userInitiated) {
      var v = this.#findVariant(sizeValue);
      if (!v) return;
      this.currentVariant = v;

      // Unit price line (e.g. "$2.87 / can") — informational.
      // Unit price is NOT painted here — it depends on the active purchase type as
      // well as the size, so it is refreshed from #refreshActivePrice() below.

      // Hidden form input.
      if (this.variantIdInput) this.variantIdInput.value = v.id;

      // PER-VARIANT SUBSCRIBE GATE (critical). Subscribe & Save is allocated per
      // VARIANT in Loop, not per product: cans only carry the plan on 12/16/24-packs,
      // NOT the 8-pack. The server-side {% if has_subscription %} is product-level, so
      // without this the Subscribe card rendered and was selectable on the 8-pack and
      // posted a selling_plan the variant does not support. Hide the card and force
      // one-time whenever the SELECTED variant has no allocation of its own.
      this.#applySubscribeAvailability();

      // Recompute the subscribe price shown in the Subscribe card for THIS variant,
      // then paint the active price + ATC label per the current purchase type.
      this.#refreshSubscribeDisplay();
      this.#refreshActivePrice();

      // Reflect availability on the button (main + sticky).
      if (this.atcButton) {
        if (v.available === false) {
          this.atcButton.setAttribute('disabled', 'disabled');
        } else {
          this.atcButton.removeAttribute('disabled');
        }
      }
      if (this.stickyButton) {
        if (v.available === false) {
          this.stickyButton.setAttribute('disabled', 'disabled');
        } else {
          this.stickyButton.removeAttribute('disabled');
        }
      }
      this.#updateNotifyMe(v);

      // Keep the price-bearing SAVE chips visually in sync with selection.
      this.querySelectorAll('[data-amass-size-chip]').forEach(function (chip) {
        var on = chip.getAttribute('data-size-value') === sizeValue;
        chip.classList.toggle('is-selected', on);
        chip.setAttribute('aria-checked', on ? 'true' : 'false');
      });
    }

    /* ---- subscribe price math (NATIVE selling-plan allocation prices) ----- */
    // Is Subscribe & Save actually allocated to the CURRENTLY selected variant?
    // The subscribe map only contains variants Loop allocated the plan to, so
    // presence in the map IS the per-variant availability signal.
    #subscribeAllowedForVariant() {
      return !!this.#subEntry();
    }

    // Show/hide the Subscribe card for the selected variant, and never leave the
    // shopper on a subscribe selection the variant cannot fulfil.
    #applySubscribeAvailability() {
      var allowed = this.#subscribeAllowedForVariant();
      if (this.subscribeCardEl) {
        this.subscribeCardEl.hidden = !allowed;
      }
      if (!allowed && this.purchaseType === 'subscribe') {
        // Force the radio + state back to one-time; this also clears selling_plan.
        // Remember that WE did this rather than the shopper, so the subscribe-first
        // default can be restored if they move back to a supported size.
        this.purchaseTypeCoerced = true;
        var oneTime = this.querySelector('[data-amass-purchase-radio][value="onetime"]');
        if (oneTime) oneTime.checked = true;
        this.#applyPurchaseType('onetime');
      } else if (allowed && this.purchaseTypeCoerced) {
        // The shopper never chose One-Time — an unsupported size (can 8-pack) forced
        // it. Now that the selected size supports Subscribe & Save again, restore the
        // subscribe-first default. Without this, tapping 8 Pack and then any other
        // size silently left the page on One-Time for the rest of the visit.
        // An explicit click on either radio clears the flag (see #wirePurchaseType),
        // so a shopper who genuinely picked One-Time is never overridden.
        this.purchaseTypeCoerced = false;
        var subRadio = this.querySelector('[data-amass-purchase-radio][value="subscribe"]');
        if (subRadio) subRadio.checked = true;
        this.#applyPurchaseType('subscribe');
      }
      // Keep the fieldset legend honest: with no subscribe option there is no choice
      // to make, so the "Purchase Options" heading is redundant on that variant.
      if (this.purchaseLegendEl) this.purchaseLegendEl.hidden = !allowed;
    }

    #subEntry() {
      if (!this.hasSubscription || !this.currentVariant) return null;
      var byId = this.subData.variants || {};
      return byId[this.currentVariant.id] || null;
    }

    // Paint the Subscribe card's discounted price + struck-through one-time price
    // for the current variant. Also keep the One-Time card's price in sync.
    #refreshSubscribeDisplay() {
      if (this.onetimePriceEl && this.currentVariant) {
        this.onetimePriceEl.textContent = formatMoney(this.currentVariant.price, this.moneyFormat);
      }
      if (!this.hasSubscription) return;
      var entry = this.#subEntry();
      if (this.subPriceEl) {
        this.subPriceEl.textContent = entry
          ? formatMoney(entry.subPrice, this.moneyFormat)
          : (this.currentVariant ? formatMoney(this.currentVariant.price, this.moneyFormat) : '');
      }
      if (this.subWasEl) {
        if (entry && entry.wasPrice && entry.wasPrice !== entry.subPrice) {
          this.subWasEl.textContent = formatMoney(entry.wasPrice, this.moneyFormat);
          this.subWasEl.hidden = false;
        } else {
          this.subWasEl.hidden = true;
        }
      }
    }

    // The headline price + ATC label reflect the active purchase type:
    // subscribe -> discounted allocation price; one-time -> the real variant price.
    #activePrice() {
      var v = this.currentVariant;
      if (!v) return 0;
      if (this.purchaseType === 'subscribe') {
        var entry = this.#subEntry();
        if (entry && typeof entry.subPrice !== 'undefined') return entry.subPrice;
      }
      return v.price;
    }

    // "$X / can" (or "/ serving") for the CURRENT selection. It has to track the
    // active purchase type, not just the size: a subscription is 10% cheaper, so
    // deriving the unit price from the one-time price alone printed a number that
    // contradicted the headline the shopper was looking at (e.g. "$40.50" beside
    // "$1.40 / serving", where $1.40 is 32 servings at the $45.00 one-time price).
    #refreshUnitPrice() {
      if (!this.unitPriceEl) return;
      var v = this.currentVariant;
      // Pack size comes from the variant map. Older payloads carried only the
      // server's one-time unit price, so derive the count from it as a fallback.
      var count = 0;
      if (v) {
        if (v.count) count = v.count;
        else if (v.unit) count = Math.round(v.price / v.unit);
      }
      if (!v || !count || !v.unitLabel) {
        this.unitPriceEl.hidden = true;
        return;
      }
      var unit = Math.round(this.#activePrice() / count);
      this.unitPriceEl.textContent = formatMoney(unit, this.moneyFormat) + ' / ' + v.unitLabel;
      this.unitPriceEl.hidden = false;
    }

    // Per-unit price printed on every SIZE chip, so the value ladder is readable
    // without clicking through each size. This replaced the old "SAVE 15% / 20%"
    // labels, which were a comparison against the smallest pack rather than a
    // discount that existed at checkout. Like the headline unit price, each chip
    // tracks the active purchase type — otherwise the chips would quote one-time
    // maths while the page is on the subscribe default.
    #refreshChipUnitPrices() {
      var self = this;

      // BUNDLE chips (snippets/flavor-selector.liquid) link to a SIBLING product, so
      // their per-unit price cannot come from this product's variant map. The server
      // renders both figures onto the element and this just picks the one matching the
      // active purchase type — otherwise the bundle would quote one-time maths while
      // the size chips beside it quote subscribe, which is the exact contradiction the
      // per-chip unit price exists to remove.
      var bundleEls = this.querySelectorAll('[data-amass-bundle-unit]');
      bundleEls.forEach(function (el) {
        var key = self.purchaseType === 'subscribe' ? 'data-unit-subscribe' : 'data-unit-onetime';
        var text = el.getAttribute(key) || el.getAttribute('data-unit-onetime');
        if (text) el.textContent = text;
      });

      var els = this.querySelectorAll('[data-amass-chip-unit]');
      if (!els.length) return;
      els.forEach(function (el) {
        var chip = el.closest('[data-amass-size-chip]');
        if (!chip) return;
        var sizeValue = chip.getAttribute('data-size-value');
        var v = null;
        for (var i = 0; i < self.variants.length; i++) {
          if (self.variants[i].size === sizeValue) { v = self.variants[i]; break; }
        }
        if (!v) return;
        var count = parseInt(el.getAttribute('data-chip-count'), 10) || v.count || 0;
        if (!count) return;
        // Subscribe pricing is per VARIANT: a size with no allocation (can 8-packs)
        // correctly keeps quoting its one-time unit price even while Subscribe is on.
        var price = v.price;
        if (self.purchaseType === 'subscribe' && self.subData && self.subData.variants) {
          var entry = self.subData.variants[v.id];
          if (entry && typeof entry.subPrice !== 'undefined') price = entry.subPrice;
        }
        el.textContent = formatMoney(Math.round(price / count), self.moneyFormat) + '/' + (v.unitLabel || 'unit');
      });
    }

    // SHIPPING LINE on every size chip and on the bundle chip: the earned
    // "SHIPS FREE" badge, or nothing. A sub-threshold chip stays silent — the
    // "+$X FOR FREE SHIPPING" shortfall text was cut (Sarah, 2026-08-31: too long,
    // reads badly, breaks the chip on mobile); the below-ATC progress nudge
    // (#refreshShipNudge) still carries the "$X away" message for that case.
    //
    // A SUBSCRIPTION ALWAYS SHIPS FREE regardless of total (same rule the below-ATC
    // nudge applies). That is not a detail: the 32-Count's subscribe price is $40.50,
    // BELOW the $45 bar, so a price-only rule would strip its badge the moment the
    // page opened on Subscribe & Save — which is the default. The badge therefore
    // never derives from a pack-name token, only threshold vs the real price.
    #refreshChipShipping() {
      var els = this.querySelectorAll('[data-amass-chip-ship]');
      if (!els.length) return;
      var subscribing = this.purchaseType === 'subscribe';
      var qty = this.qtyInput ? parseInt(this.qtyInput.value, 10) || 1 : 1;
      els.forEach(function (el) {
        var threshold = parseInt(el.getAttribute('data-threshold'), 10) || 0;
        var price = parseInt(el.getAttribute('data-chip-price'), 10);
        // Loop allocates the plan per VARIANT: a size with no allocation cannot be
        // subscribed, so it must be judged on its one-time price even while the rest
        // of the page is on Subscribe. Server-rendered by the same resolution.
        var subEligible = el.getAttribute('data-chip-sub') !== '0';
        var text = '';
        if (subscribing && subEligible) {
          text = 'SHIPS FREE';
        } else if (!isNaN(price) && threshold > 0) {
          // Quantity applies to the SIZE chips (they are this product's own variants).
          // A bundle chip is a link to another product, so it is always priced at one.
          var inSizeChip = !!el.closest('[data-amass-size-chip]');
          var lineTotal = price * (inSizeChip ? qty : 1);
          if (lineTotal >= threshold) text = 'SHIPS FREE';
        }
        el.textContent = text;
      });
    }

    // Both shipping surfaces move together: the per-chip line and the below-ATC
    // progress nudge read the same threshold, so they must never be refreshed apart.
    #refreshShipping() {
      this.#refreshChipShipping();
      this.#refreshShipNudge();
    }

    #refreshActivePrice() {
      if (!this.currentVariant) return;
      var price = this.#activePrice();
      if (this.priceEl) this.priceEl.textContent = formatMoney(price, this.moneyFormat);
      this.#refreshUnitPrice();
      this.#refreshChipUnitPrices();
      this.#updateAtcLabel(this.currentVariant, price);
      // Keep the sticky bar's price + button in lockstep with the main ATC.
      if (this.stickyPriceEl) this.stickyPriceEl.textContent = formatMoney(price, this.moneyFormat);
      this.#updateStickyLabel(this.currentVariant);
      // Free-shipping progress reflects the active price x quantity.
      this.#refreshShipping();
    }

    // Free-shipping nudge: progress toward the threshold for the CURRENT selection
    // (active one-time/subscribe price x quantity). A subscription always ships
    // free, so it shows the unlocked state regardless of total. Mirrors the cart
    // drawer free-shipping bar's logic (snippets/free-shipping-bar.liquid).
    #refreshShipNudge() {
      if (!this.shipNudgeEl || !this.currentVariant) return;
      var threshold = parseInt(this.shipNudgeEl.getAttribute('data-threshold'), 10) || 0;
      var qty = this.qtyInput ? (parseInt(this.qtyInput.value, 10) || 1) : 1;
      var subscribing = this.purchaseType === 'subscribe';
      var lineTotal = this.#activePrice() * qty;
      var unlocked = subscribing || (threshold > 0 && lineTotal >= threshold);
      // One-time orders that already clear the threshold surface SHIPS FREE on the
      // size chip, so hide the (less prominent) below-ATC nudge in that case. The
      // nudge is kept for the actionable "$X away" case and for subscriptions.
      var oneTimeUnlocked = unlocked && !subscribing;
      this.shipNudgeEl.hidden = oneTimeUnlocked;
      if (oneTimeUnlocked) return;
      var remaining = Math.max(0, threshold - lineTotal);
      var progress = threshold > 0 ? Math.round((lineTotal / threshold) * 100) : 100;
      if (progress > 100) progress = 100;
      if (unlocked) progress = 100;
      if (this.shipNudgeMsgEl) {
        if (subscribing) {
          this.shipNudgeMsgEl.textContent = 'Your subscription ships free';
        } else if (unlocked) {
          this.shipNudgeMsgEl.textContent = 'This order ships free';
        } else {
          this.shipNudgeMsgEl.innerHTML =
            'You’re <span data-amass-shipnudge-remaining>' +
            formatMoney(remaining, this.moneyFormat) +
            '</span> away from free shipping';
        }
      }
      if (this.shipNudgeFillEl) this.shipNudgeFillEl.style.width = progress + '%';
      this.shipNudgeEl.classList.toggle('is-unlocked', unlocked);
    }

    // Sticky button label mirrors availability (the sticky bar shows the PRICE in
    // its own element, so the button text stays a plain "Add to Cart" / "Sold Out").
    #updateStickyLabel(v) {
      if (!this.stickyLabelEl) return;
      var base = this.stickyLabelEl.getAttribute('data-atc-base') || 'Add to Cart';
      if (v && v.available === false) {
        this.stickyLabelEl.textContent = this.stickyLabelEl.getAttribute('data-atc-soldout') || 'Sold Out';
      } else {
        this.stickyLabelEl.textContent = base;
      }
    }

    #updateAtcLabel(v, price) {
      if (!this.atcLabelEl) return;
      var base = this.atcLabelEl.getAttribute('data-atc-base') || 'Add to Cart';
      if (v && v.available === false) {
        this.atcLabelEl.textContent = this.atcLabelEl.getAttribute('data-atc-soldout') || 'Sold Out';
      } else if (v) {
        if (typeof price !== 'number') price = v.price;
        this.atcLabelEl.textContent = base + ' · ' + formatMoney(price, this.moneyFormat);
      }
    }

    /* ---- BACK-IN-STOCK SIGN-UP (sold-out variants) ------------------------- */
    // Show the Klaviyo sign-up card only while the SELECTED variant is sold out,
    // and flag the controller so CSS hides the free-ship nudge in that state.
    #updateNotifyMe(v) {
      var soldOut = !!(v && v.available === false);
      this.classList.toggle('is-soldout', soldOut);
      if (this.notifyEl) this.notifyEl.hidden = !soldOut;
    }

    /* ---- PURCHASE TYPE: one-time vs subscribe (+ delivery frequency) ------ */
    #wirePurchaseType() {
      var self = this;
      // Purchase-type radios (one-time / subscribe).
      this.querySelectorAll('[data-amass-purchase-radio]').forEach(function (radio) {
        radio.addEventListener('change', function () {
          if (!radio.checked) return;
          // An explicit choice by the shopper — it is theirs to keep from here on.
          self.purchaseTypeCoerced = false;
          self.#applyPurchaseType(radio.value);
        });
      });
      // Delivery-frequency dropdown updates the selling_plan (and repaints the
      // price + ATC, in case a future plan carries a different allocation) when
      // Subscribe is the active type.
      if (this.freqSelect) {
        this.freqSelect.addEventListener('change', function () {
          if (self.purchaseType === 'subscribe') {
            self.#syncSellingPlan();
            self.#refreshActivePrice();
          }
        });
      }
    }

    // Adopt the server's pre-selected purchase type. Called from connectedCallback
    // AFTER the variant is known (see the note there), never from #wirePurchaseType.
    #initPurchaseType() {
      var checked = this.querySelector('[data-amass-purchase-radio]:checked');
      this.#applyPurchaseType(checked ? checked.value : 'onetime');
    }

    #applyPurchaseType(type) {
      // Subscribe requires BOTH a product-level plan group AND an allocation on the
      // selected variant. #subEntry() is the per-variant check; without it an
      // unsupported variant (can 8-pack) could be switched to subscribe.
      var wantsSubscribe = type === 'subscribe' && this.hasSubscription && !!this.#subEntry();
      this.purchaseType = wantsSubscribe ? 'subscribe' : 'onetime';
      var active = this.purchaseType;

      // If subscribe was REFUSED for this variant, force the radio DOM back in line with
      // the coerced state. Without this the subscribe radio could stay .checked while the
      // controller is on one-time, and because clicking an already-checked radio fires no
      // change event, a later legitimate click on Subscribe would appear to do nothing.
      if (type === 'subscribe' && !wantsSubscribe) {
        var oneTimeRadio = this.querySelector('[data-amass-purchase-radio][value="onetime"]');
        if (oneTimeRadio) oneTimeRadio.checked = true;
      }

      // Card selected states.
      this.querySelectorAll('[data-amass-purchase-card]').forEach(function (card) {
        var on = card.getAttribute('data-purchase-type') === active;
        card.classList.toggle('is-selected', on);
      });

      // Reveal the frequency dropdown only when subscribing. Two coordinated
      // signals: (a) a state class on the fieldset that the CSS keys off to flip
      // the wrapper from display:none -> flex, and (b) the [hidden] attribute on
      // the wrapper (honoured by CSS too). One-Time => hidden on both.
      var subscribing = active === 'subscribe';
      if (this.purchaseEl) this.purchaseEl.classList.toggle('is-subscribe-active', subscribing);
      if (this.freqWrap) this.freqWrap.hidden = !subscribing;

      // Write / clear the selling_plan id, then repaint price + ATC.
      this.#syncSellingPlan();
      this.#refreshActivePrice();
    }

    // selling_plan hidden input: the chosen frequency's plan id when subscribing,
    // empty for one-time. Ids come from the native selling-plan map (no hardcode).
    #syncSellingPlan() {
      if (!this.sellingPlanInput) return;
      // Last line of defence: only ever post a selling_plan when the SELECTED variant
      // actually has that plan allocated. Guards against posting a plan id the variant
      // does not support (can 8-packs carry no Subscribe & Save allocation).
      if (
        this.purchaseType === 'subscribe' &&
        this.#subEntry() &&
        this.freqSelect &&
        this.freqSelect.value
      ) {
        this.sellingPlanInput.value = this.freqSelect.value;
        this.sellingPlanInput.disabled = false;
      } else {
        // Disable (not just blank) so no empty selling_plan param is posted on a
        // one-time add — Shopify then adds the plain variant.
        this.sellingPlanInput.value = '';
        this.sellingPlanInput.disabled = true;
      }
    }

    /* ---- FLAVOR: route to sibling product (US-001 map) ------------------- */
    #wireFlavorSelector() {
      var self = this;
      this.querySelectorAll('[data-amass-flavor]').forEach(function (el) {
        // Current flavor renders as a non-link; siblings carry data-flavor-url.
        var url = el.getAttribute('data-flavor-url');
        if (!url) return;
        el.addEventListener('click', function (e) {
          e.preventDefault();
          self.#routeToFlavor(url);
        });
        // keyboard activation for non-anchor controls
        el.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            self.#routeToFlavor(url);
          }
        });
      });
    }

    #routeToFlavor(baseUrl) {
      // Preserve the chosen size where the sibling product also offers it.
      var checked = this.querySelector('input[name="amass-size"]:checked');
      var target = baseUrl;
      if (checked && checked.value) {
        var sep = baseUrl.indexOf('?') === -1 ? '?' : '&';
        target = baseUrl + sep + 'amass_size=' + encodeURIComponent(checked.value);
      }
      window.location.href = target;
    }

    /* ---- QUANTITY stepper ------------------------------------------------ */
    #wireQuantity() {
      var self = this;
      var dec = this.querySelector('[data-amass-qty-dec]');
      var inc = this.querySelector('[data-amass-qty-inc]');
      if (!this.qtyInput) return;
      function clamp(n) {
        var min = parseInt(self.qtyInput.getAttribute('min'), 10) || 1;
        if (isNaN(n) || n < min) return min;
        return n;
      }
      if (dec) dec.addEventListener('click', function () {
        self.qtyInput.value = clamp(parseInt(self.qtyInput.value, 10) - 1);
        self.#refreshShipping();
      });
      if (inc) inc.addEventListener('click', function () {
        self.qtyInput.value = clamp(parseInt(self.qtyInput.value, 10) + 1);
        self.#refreshShipping();
      });
      this.qtyInput.addEventListener('change', function () {
        self.qtyInput.value = clamp(parseInt(self.qtyInput.value, 10));
        self.#refreshShipping();
      });
    }

    /* ---- ADD TO CART: native cart + drawer (US-004) ---------------------- */
    #wireAddToCart() {
      var self = this;
      var form = this.querySelector('form[data-amass-product-form]');
      if (!form) return;
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        self.#addToCart(form);
      });
    }

    /* ---- STICKY MOBILE ATC BAR (US-005 conversion) ----------------------- */
    // The sticky bar reuses the SAME product form and #addToCart path, so the
    // variant id + selling_plan hidden inputs (kept current by the size/purchase
    // handlers) travel with the add and the US-004 drawer opens exactly as the
    // main button. Visibility is driven by an IntersectionObserver on the main
    // ATC: the bar shows once the main button leaves the viewport and hides when
    // it returns or when the footer scrolls in (so it never covers the footer).
    #wireStickyBar() {
      var self = this;
      if (!this.stickyBar) return;
      var form = this.querySelector('form[data-amass-product-form]');
      if (!form) return;

      // Same add as the main button (current variant + selling_plan).
      if (this.stickyButton) {
        this.stickyButton.addEventListener('click', function () {
          if (self.stickyButton.hasAttribute('disabled')) return;
          self.#addToCart(form);
        });
      }

      // No IntersectionObserver (old browsers): reveal the bar unconditionally so
      // it still works. The reveal is keyed SOLELY on the .is-visible class (the
      // CSS base state keeps it off-screen/transparent/out of the tab order until
      // then) -- no dependency on any [hidden] attribute or Horizon data-stuck.
      if (typeof window.IntersectionObserver !== 'function') {
        this.stickyBar.classList.add('is-visible');
        this.stickyBar.setAttribute('aria-hidden', 'false');
        return;
      }

      this.#stickyMainOut = false;   // main ATC has left the viewport
      this.#stickyFooterIn = false;  // footer is on screen

      // Watch the main ATC button. On mobile it sits low in the buybox, BELOW the
      // initial viewport, so a plain !isIntersecting is true in TWO cases: before
      // the shopper has scrolled DOWN to it, and after they have scrolled PAST it
      // -- which would wrongly reveal the bar at the top of the page. Use the sign
      // of the observed rect instead: entry.boundingClientRect.bottom < 0 means the
      // whole button has scrolled ABOVE the top of the viewport (shopper is past
      // it). While the button is in view or still below (bottom >= 0), keep the bar
      // hidden. This is the signal #updateStickyVisibility() uses to reveal the bar.
      if (this.atcButton) {
        var atcObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            self.#stickyMainOut = entry.boundingClientRect.bottom < 0;
            self.#updateStickyVisibility();
          });
        }, { threshold: 0 });
        atcObserver.observe(this.atcButton);
      }

      // Watch the footer so the bar retracts before it can overlap it. The footer
      // is a TALL element (its top edge peeks into the viewport ~a full screen
      // before the shopper reaches the actual page bottom), so a raw
      // entry.isIntersecting on a threshold:0 observer flips true the instant the
      // footer's top crosses the bottom of the viewport -- which, on a short PDP,
      // is only a few hundred px after the main ATC leaves. That collapsed the
      // reveal window to a sliver and made the bar look like it never showed. The
      // bar (fixed, one bar-height tall, pinned to the viewport bottom) only truly
      // OVERLAPS the footer once the footer's BOTTOM has scrolled up to within a
      // bar-height of the viewport bottom -- i.e. the page is essentially bottomed
      // out and the footer's own final CTA/legal is on screen. So compute the
      // collision from the footer RECT (viewport- and height-agnostic; no hardcoded
      // offsets) instead of first-pixel intersection. The IntersectionObserver is
      // kept to wake re-evaluation as the footer approaches; a passive scroll
      // listener keeps re-evaluating while the footer is on screen (the observer
      // does not re-fire on every scroll tick once the footer is partly in view).
      var footer =
        document.querySelector('.amass-footer') ||
        document.querySelector('.shopify-section-group-footer-group') ||
        document.querySelector('footer');
      if (footer) {
        var footerCollides = function (el) {
          var barH = self.stickyBar ? self.stickyBar.offsetHeight : 0;
          var rect = el.getBoundingClientRect();
          // True only when the footer's bottom is within a bar-height of the
          // viewport bottom (the page is at/near the end and the bar would sit on
          // top of the footer's final content).
          return rect.bottom <= window.innerHeight + barH;
        };
        var footerObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            self.#stickyFooterIn = footerCollides(entry.target);
            self.#updateStickyVisibility();
          });
        }, { threshold: 0 });
        footerObserver.observe(footer);
        // Re-evaluate the footer collision on scroll (passive) so the bar retracts
        // exactly when the footer reaches the bottom band and re-appears on scroll-up.
        window.addEventListener('scroll', function () {
          self.#stickyFooterIn = footerCollides(footer);
          self.#updateStickyVisibility();
        }, { passive: true });
      }
    }

    #stickyMainOut = false;
    #stickyFooterIn = false;

    #updateStickyVisibility() {
      if (!this.stickyBar) return;
      var show = this.#stickyMainOut && !this.#stickyFooterIn;
      // Reveal/hide is driven ENTIRELY by toggling .is-visible: the CSS base rule
      // keeps the bar off-screen (translateY(100%)), transparent, non-interactive
      // and out of the tab order (visibility:hidden); .is-visible brings it fully
      // on-screen at bottom:0 and re-enables focus. aria-hidden mirrors the state
      // for assistive tech. Deliberately NOT keyed on Horizon's data-stuck.
      if (show) {
        this.stickyBar.classList.add('is-visible');
        this.stickyBar.setAttribute('aria-hidden', 'false');
      } else {
        this.stickyBar.classList.remove('is-visible');
        this.stickyBar.setAttribute('aria-hidden', 'true');
      }
    }

    #addToCart(form) {
      var self = this;
      if (this.atcButton && this.atcButton.hasAttribute('disabled')) return;

      var formData = new FormData(form);

      // Include the cart-drawer sections so Horizon can re-render them.
      var sectionIds = [];
      document.querySelectorAll('cart-items-component, cart-drawer-component').forEach(function (el) {
        if (el.dataset && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
      });
      if (sectionIds.length) formData.append('sections', sectionIds.join(','));

      if (this.atcButton) this.atcButton.classList.add('is-loading');
      if (this.stickyButton) this.stickyButton.classList.add('is-loading');

      fetch(cartAddUrl(), {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      })
        .then(function (r) { return r.json(); })
        .then(function (item) {
          if (item && item.status) {
            // Error from Shopify (e.g. variant unavailable)
            self.#dispatchCartError(item);
            return;
          }
          self.#refreshCart();
        })
        .catch(function () { /* swallow — leave UI usable */ })
        .finally(function () {
          if (self.atcButton) self.atcButton.classList.remove('is-loading');
          if (self.stickyButton) self.stickyButton.classList.remove('is-loading');
        });
    }

    #refreshCart() {
      var self = this;
      fetch(routesRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          // Fire the same event Horizon's cart components + US-004 drawer/FSB use.
          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: cart,
                sourceId: self.id || 'amass-product',
                data: { source: 'amass-product', itemCount: cart.item_count },
              },
            })
          );
          self.#openCartDrawer();
        })
        .catch(function () {});
    }

    #openCartDrawer() {
      // Prefer Horizon's drawer API if present; otherwise click its toggle.
      var drawer = document.querySelector('cart-drawer-component');
      if (drawer) {
        if (typeof drawer.open === 'function') { drawer.open(); return; }
        if (typeof drawer.showDialog === 'function') { drawer.showDialog(); return; }
        var dialog = drawer.querySelector('dialog');
        if (dialog && typeof dialog.showModal === 'function') { dialog.showModal(); return; }
      }
      var toggle = document.querySelector('[data-cart-drawer-toggle], a[href$="/cart"], .header-actions__cart-icon');
      if (toggle && toggle.click) toggle.click();
    }

    #dispatchCartError(payload) {
      document.dispatchEvent(
        new CustomEvent('cart:error', {
          bubbles: true,
          detail: { data: { message: (payload && payload.description) || (payload && payload.message) || '' } },
        })
      );
    }
  }

  /* ======================================================================= */
  /* <amass-accordion> — How To Use / Ingredients / Nutrition                 */
  /* ======================================================================= */
  class AmassAccordion extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.querySelectorAll('[data-amass-accordion-trigger]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var item = btn.closest('[data-amass-accordion-item]');
          if (!item) return;
          var open = item.hasAttribute('data-open');
          // Single-open behaviour is nicer here; allow multiple if data-multi set.
          if (!self.hasAttribute('data-multi')) {
            self.querySelectorAll('[data-amass-accordion-item][data-open]').forEach(function (other) {
              if (other !== item) {
                other.removeAttribute('data-open');
                var t = other.querySelector('[data-amass-accordion-trigger]');
                if (t) t.setAttribute('aria-expanded', 'false');
              }
            });
          }
          if (open) {
            item.removeAttribute('data-open');
            btn.setAttribute('aria-expanded', 'false');
          } else {
            item.setAttribute('data-open', '');
            btn.setAttribute('aria-expanded', 'true');
          }
        });
      });
    }
  }

  /* ---- register ---------------------------------------------------------- */
  function define(name, ctor) {
    if (!customElements.get(name)) customElements.define(name, ctor);
  }
  define('amass-product', AmassProduct);
  define('amass-accordion', AmassAccordion);
})();




